import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, Lock, Mail } from 'lucide-react';

import { Button } from '../../../v2/components/Button';
import { Input } from '../../../v2/components/Input';
import { cn } from '../../../v2/lib/cn';

const schema = z.object({
    email: z
        .string()
        .min(1, 'Adresa de email nu poate fi goala')
        .email('Adresa de email este invalida'),
    password: z.string().min(1, 'Parola nu poate fi goala'),
    rememberMe: z.boolean(),
});

// 16px font-size on mobile prevents iOS Safari from zooming on focus; shrink
// to 14px from sm: up where desktop dialogs expect denser inputs. Inputs are
// h-12 on mobile (≥44px Apple HIG tap target) and h-10 from sm: up.
const inputSizing = 'h-12 text-base sm:h-10 sm:text-sm';

const passwordInputClasses = cn(
    'flex w-full rounded-md border border-solid border-gray-300 bg-white py-2 pl-10 pr-12 text-gray-900 placeholder:text-gray-400',
    inputSizing,
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:border-transparent',
    'disabled:cursor-not-allowed disabled:opacity-50',
    'dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500',
);

export default function LoginForm({ onSubmit }) {
    const [showPassword, setShowPassword] = useState(false);
    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm({
        resolver: zodResolver(schema),
        defaultValues: { email: '', password: '', rememberMe: true },
    });

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5" noValidate>
            <div>
                <label
                    htmlFor="login-email"
                    className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-200"
                >
                    Email
                </label>
                <Input
                    id="login-email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    enterKeyHint="next"
                    placeholder="nume@exemplu.ro"
                    icon={Mail}
                    aria-invalid={!!errors.email}
                    className={inputSizing}
                    {...register('email')}
                />
                {errors.email && (
                    <p className="mt-1.5 text-xs text-red-600 dark:text-red-400">
                        {errors.email.message}
                    </p>
                )}
            </div>

            <div>
                <label
                    htmlFor="login-password"
                    className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-200"
                >
                    Parola
                </label>
                <div className="relative w-full">
                    <Lock
                        aria-hidden="true"
                        className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400 sm:h-4 sm:w-4"
                    />
                    <input
                        id="login-password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck={false}
                        enterKeyHint="go"
                        placeholder="Parola"
                        aria-invalid={!!errors.password}
                        className={passwordInputClasses}
                        {...register('password')}
                    />
                    <button
                        type="button"
                        onClick={() => setShowPassword((s) => !s)}
                        aria-label={showPassword ? 'Ascunde parola' : 'Afiseaza parola'}
                        aria-pressed={showPassword}
                        className="absolute right-1 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200 sm:h-8 sm:w-8"
                    >
                        {showPassword ? (
                            <EyeOff aria-hidden="true" className="h-5 w-5 sm:h-4 sm:w-4" />
                        ) : (
                            <Eye aria-hidden="true" className="h-5 w-5 sm:h-4 sm:w-4" />
                        )}
                    </button>
                </div>
                {errors.password && (
                    <p className="mt-1.5 text-xs text-red-600 dark:text-red-400">
                        {errors.password.message}
                    </p>
                )}
            </div>

            <label className="-mx-2 inline-flex select-none items-center gap-3 rounded-md px-2 py-2 active:bg-gray-100 sm:py-1 dark:active:bg-gray-800">
                <input
                    type="checkbox"
                    className="h-5 w-5 rounded border-solid border-gray-300 text-blue-600 focus:ring-blue-500 sm:h-4 sm:w-4 dark:border-gray-600 dark:bg-gray-800"
                    {...register('rememberMe')}
                />
                <span className="text-base text-gray-700 dark:text-gray-200 sm:text-sm">Tine-ma minte</span>
            </label>

            <Button
                type="submit"
                size="lg"
                disabled={isSubmitting}
                className="w-full sm:h-10 sm:text-sm"
            >
                {isSubmitting ? 'Se conecteaza...' : 'Autentificare'}
            </Button>
        </form>
    );
}
